'use client';
import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import StockCountEntryModal from './StockCountEntryModal';
import StockCountApprovalModal from './StockCountApprovalModal';
import NonVarianceReportModal from './NonVarianceReportModal';
import { toast } from 'sonner';

export type CountStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';

export interface CountLineItem {
  id: string;
  itemCode: string;
  itemName: string;
  category: string;
  uom: string;
  systemQty: number;
  physicalQty: number | null;
  variance: number | null;
  variancePct: number | null;
  unitCost: number;
  varianceValue: number | null;
  notes: string;
}

export interface StockCount {
  id: string;
  countNumber: string;
  site: string;
  countDate: string;
  createdBy: string;
  status: CountStatus;
  lineItems: CountLineItem[];
  totalItems: number;
  variantItems: number;
  approvedBy?: string;
  approvedDate?: string;
  rejectionReason?: string;
  adjustmentPosted: boolean;
}

export const MOCK_COUNTS: StockCount[] = [
  {
    id: 'cnt-001',
    countNumber: 'CNT-2026-0042',
    site: 'Site Alpha',
    countDate: '28 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'PENDING_APPROVAL',
    totalItems: 6,
    variantItems: 2,
    adjustmentPosted: false,
    lineItems: [
      { id: 'cli-001-1', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', category: 'RAW_MATERIAL', uom: 'MT', systemQty: 18, physicalQty: 16, variance: -2, variancePct: -11.11, unitCost: 680, varianceValue: -1360, notes: 'Two bundles not found in designated bay' },
      { id: 'cli-001-2', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', category: 'RAW_MATERIAL', uom: 'BAG', systemQty: 820, physicalQty: 820, variance: 0, variancePct: 0, unitCost: 7.20, varianceValue: 0, notes: '' },
      { id: 'cli-001-3', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', uom: 'EA', systemQty: 3, physicalQty: 4, variance: 1, variancePct: 33.33, unitCost: 142, varianceValue: 142, notes: 'Extra unit found — possibly returned without GI reversal' },
      { id: 'cli-001-4', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', category: 'PPE', uom: 'EA', systemQty: 18, physicalQty: 18, variance: 0, variancePct: 0, unitCost: 12.50, varianceValue: 0, notes: '' },
      { id: 'cli-001-5', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', uom: 'EA', systemQty: 12, physicalQty: 12, variance: 0, variancePct: 0, unitCost: 89.50, varianceValue: 0, notes: '' },
      { id: 'cli-001-6', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', category: 'TOOL', uom: 'EA', systemQty: 7, physicalQty: 7, variance: 0, variancePct: 0, unitCost: 68, varianceValue: 0, notes: '' },
    ],
  },
  {
    id: 'cnt-002',
    countNumber: 'CNT-2026-0041',
    site: 'Warehouse B',
    countDate: '25 Jul 2026',
    createdBy: 'Anita Desai',
    status: 'APPROVED',
    totalItems: 5,
    variantItems: 1,
    adjustmentPosted: true,
    approvedBy: 'Arjun Mehta',
    approvedDate: '26 Jul 2026',
    lineItems: [
      { id: 'cli-002-1', itemCode: 'LUB-HYD-46', itemName: 'Hydraulic Oil ISO 46', category: 'CONSUMABLE', uom: 'L', systemQty: 14, physicalQty: 12, variance: -2, variancePct: -14.29, unitCost: 4.80, varianceValue: -9.60, notes: 'Spillage during transfer — documented' },
      { id: 'cli-002-2', itemCode: 'MECH-BRNG-6205', itemName: 'Deep Groove Ball Bearing 6205', category: 'SPARE_PART', uom: 'EA', systemQty: 4, physicalQty: 4, variance: 0, variancePct: 0, unitCost: 18.40, varianceValue: 0, notes: '' },
      { id: 'cli-002-3', itemCode: 'SEAL-ORING-32', itemName: 'O-Ring NBR 32mm ID', category: 'SPARE_PART', uom: 'EA', systemQty: 148, physicalQty: 148, variance: 0, variancePct: 0, unitCost: 0.45, varianceValue: 0, notes: '' },
      { id: 'cli-002-4', itemCode: 'TOOL-GRND-4IN', itemName: 'Angle Grinder 4" 850W', category: 'TOOL', uom: 'EA', systemQty: 7, physicalQty: 7, variance: 0, variancePct: 0, unitCost: 68, varianceValue: 0, notes: '' },
      { id: 'cli-002-5', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', category: 'CONSUMABLE', uom: 'KG', systemQty: 32, physicalQty: 32, variance: 0, variancePct: 0, unitCost: 14.20, varianceValue: 0, notes: '' },
    ],
  },
  {
    id: 'cnt-003',
    countNumber: 'CNT-2026-0040',
    site: 'Site Beta',
    countDate: '20 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'APPROVED',
    totalItems: 4,
    variantItems: 0,
    adjustmentPosted: false,
    approvedBy: 'Arjun Mehta',
    approvedDate: '21 Jul 2026',
    lineItems: [
      { id: 'cli-003-1', itemCode: 'ELE-CBL-25', itemName: 'Electrical Cable 2.5mm²', category: 'RAW_MATERIAL', uom: 'M', systemQty: 480, physicalQty: 480, variance: 0, variancePct: 0, unitCost: 1.35, varianceValue: 0, notes: '' },
      { id: 'cli-003-2', itemCode: 'WELD-ROD-308', itemName: 'Welding Electrode SS 308', category: 'CONSUMABLE', uom: 'KG', systemQty: 32, physicalQty: 32, variance: 0, variancePct: 0, unitCost: 14.20, varianceValue: 0, notes: '' },
      { id: 'cli-003-3', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves (Large)', category: 'PPE', uom: 'PR', systemQty: 24, physicalQty: 24, variance: 0, variancePct: 0, unitCost: 0.95, varianceValue: 0, notes: '' },
      { id: 'cli-003-4', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', uom: 'EA', systemQty: 12, physicalQty: 12, variance: 0, variancePct: 0, unitCost: 89.50, varianceValue: 0, notes: '' },
    ],
  },
  {
    id: 'cnt-004',
    countNumber: 'CNT-2026-0039',
    site: 'Site Gamma',
    countDate: '15 Jul 2026',
    createdBy: 'Meena Pillai',
    status: 'REJECTED',
    totalItems: 3,
    variantItems: 3,
    adjustmentPosted: false,
    rejectionReason: 'Count figures appear inconsistent with recent GRN receipts. Recount required.',
    lineItems: [
      { id: 'cli-004-1', itemCode: 'PPE-GLOVE-L', itemName: 'Nitrile Gloves (Large)', category: 'PPE', uom: 'PR', systemQty: 24, physicalQty: 10, variance: -14, variancePct: -58.33, unitCost: 0.95, varianceValue: -13.30, notes: '' },
      { id: 'cli-004-2', itemCode: 'PPE-HELM-WHT', itemName: 'Safety Helmet White (ISI)', category: 'PPE', uom: 'EA', systemQty: 18, physicalQty: 8, variance: -10, variancePct: -55.56, unitCost: 12.50, varianceValue: -125, notes: '' },
      { id: 'cli-004-3', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', uom: 'EA', systemQty: 3, physicalQty: 1, variance: -2, variancePct: -66.67, unitCost: 142, varianceValue: -284, notes: '' },
    ],
  },
  {
    id: 'cnt-005',
    countNumber: 'CNT-2026-0038',
    site: 'Site Alpha',
    countDate: '10 Jul 2026',
    createdBy: 'Rahul Kumar',
    status: 'DRAFT',
    totalItems: 4,
    variantItems: 0,
    adjustmentPosted: false,
    lineItems: [
      { id: 'cli-005-1', itemCode: 'STL-REBAR-12', itemName: 'TMT Steel Rebar 12mm', category: 'RAW_MATERIAL', uom: 'MT', systemQty: 20, physicalQty: null, variance: null, variancePct: null, unitCost: 680, varianceValue: null, notes: '' },
      { id: 'cli-005-2', itemCode: 'CHEM-CEM-OPC', itemName: 'OPC Cement 53 Grade', category: 'RAW_MATERIAL', uom: 'BAG', systemQty: 900, physicalQty: null, variance: null, variancePct: null, unitCost: 7.20, varianceValue: null, notes: '' },
      { id: 'cli-005-3', itemCode: 'SAF-FEXT-CO2', itemName: 'Fire Extinguisher CO₂ 5kg', category: 'SAFETY_ITEM', uom: 'EA', systemQty: 12, physicalQty: null, variance: null, variancePct: null, unitCost: 89.50, varianceValue: null, notes: '' },
      { id: 'cli-005-4', itemCode: 'SAF-HARN-01', itemName: 'Full Body Safety Harness', category: 'PPE', uom: 'EA', systemQty: 3, physicalQty: null, variance: null, variancePct: null, unitCost: 142, varianceValue: null, notes: '' },
    ],
  },
];

const STATUS_TABS = ['ALL', 'DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'] as const;
const SITES = ['All Sites', 'Site Alpha', 'Site Beta', 'Site Gamma', 'Warehouse B'];

const TAB_LABELS: Record<string, string> = {
  ALL: 'All',
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export default function StockCountContent() {
  const [statusTab, setStatusTab] = useState<typeof STATUS_TABS[number]>('ALL');
  const [siteFilter, setSiteFilter] = useState('All Sites');
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState<StockCount[]>(MOCK_COUNTS);
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [approvalCount, setApprovalCount] = useState<StockCount | null>(null);
  const [reportCount, setReportCount] = useState<StockCount | null>(null);

  const filtered = useMemo(() => {
    return counts.filter((c) => {
      const matchTab = statusTab === 'ALL' || c.status === statusTab;
      const matchSite = siteFilter === 'All Sites' || c.site === siteFilter;
      const matchSearch = search === '' ||
        c.countNumber.toLowerCase().includes(search.toLowerCase()) ||
        c.site.toLowerCase().includes(search.toLowerCase()) ||
        c.createdBy.toLowerCase().includes(search.toLowerCase());
      return matchTab && matchSite && matchSearch;
    });
  }, [counts, statusTab, siteFilter, search]);

  const tabCounts = useMemo(() => {
    const c: Record<string, number> = { ALL: counts.length };
    STATUS_TABS.slice(1).forEach((s) => { c[s] = counts.filter((x) => x.status === s).length; });
    return c;
  }, [counts]);

  const pendingCount = counts.filter((c) => c.status === 'PENDING_APPROVAL').length;
  const totalVarianceValue = counts
    .filter((c) => c.status !== 'REJECTED')
    .flatMap((c) => c.lineItems)
    .reduce((acc, li) => acc + (li.varianceValue ?? 0), 0);

  const handleApprove = (countId: string, reason: string) => {
    setCounts((prev) => prev.map((c) =>
      c.id === countId
        ? { ...c, status: 'APPROVED', approvedBy: 'Arjun Mehta', approvedDate: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }), adjustmentPosted: true }
        : c
    ));
    toast.success('Stock count approved. Adjustments posted to inventory.');
    setApprovalCount(null);
  };

  const handleReject = (countId: string, reason: string) => {
    setCounts((prev) => prev.map((c) =>
      c.id === countId ? { ...c, status: 'REJECTED', rejectionReason: reason } : c
    ));
    toast.error('Stock count rejected. Operator notified.');
    setApprovalCount(null);
  };

  const handleNewCount = (newCount: StockCount) => {
    setCounts((prev) => [newCount, ...prev]);
    setEntryModalOpen(false);
    toast.success(`${newCount.countNumber} created and submitted for approval.`);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-600 text-foreground">Stock Count</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Physical count entry per site — variance calculation, manager approval, and reconciliation reports
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => toast.success('Stock count register exported.')}
            className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg text-sm font-500 hover:bg-muted transition-colors bg-card"
          >
            <Icon name="ArrowDownTrayIcon" size={15} />
            Export
          </button>
          <button
            onClick={() => setEntryModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 shadow-card"
          >
            <Icon name="ClipboardDocumentCheckIcon" size={16} />
            New Count
          </button>
        </div>
      </div>

      {/* Summary KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Counts', value: counts.length, icon: 'ClipboardDocumentListIcon', color: 'text-secondary' },
          { label: 'Pending Approval', value: pendingCount, icon: 'ClockIcon', color: pendingCount > 0 ? 'text-amber-600' : 'text-muted-foreground' },
          { label: 'Approved This Month', value: counts.filter((c) => c.status === 'APPROVED').length, icon: 'CheckCircleIcon', color: 'text-teal-600' },
          {
            label: 'Net Variance Value',
            value: `€${Math.abs(totalVarianceValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
            icon: 'ScaleIcon',
            color: totalVarianceValue < 0 ? 'text-red-600' : totalVarianceValue > 0 ? 'text-teal-600' : 'text-muted-foreground',
          },
        ].map((kpi) => (
          <div key={`kpi-${kpi.label}`} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <Icon name={kpi.icon as Parameters<typeof Icon>[0]['name']} size={18} className={kpi.color} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{kpi.label}</p>
              <p className={`text-lg font-700 ${kpi.color}`}>{kpi.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Workflow info */}
      <div className="flex items-center gap-4 px-4 py-3 bg-secondary/5 border border-secondary/20 rounded-xl flex-wrap">
        {[
          { step: 1, label: 'ENTER COUNT', desc: 'Physical quantities per site', icon: 'PencilSquareIcon' },
          { step: 2, label: 'VARIANCE CALC', desc: 'Auto vs system balance', icon: 'CalculatorIcon' },
          { step: 3, label: 'MANAGER APPROVAL', desc: 'Review & post adjustments', icon: 'UserCheckIcon' },
        ].map((s, i) => (
          <React.Fragment key={`wf-${s.step}`}>
            <div className="flex items-center gap-2 text-secondary">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 bg-secondary text-white shrink-0">
                {s.step}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-600">{s.label}</p>
                <p className="text-[10px] text-muted-foreground">{s.desc}</p>
              </div>
            </div>
            {i < 2 && <Icon name="ChevronRightIcon" size={14} className="text-muted-foreground hidden sm:block" />}
          </React.Fragment>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Status tabs */}
        <div className="flex bg-muted p-1 rounded-lg shrink-0">
          {STATUS_TABS.map((tab) => (
            <button
              key={`tab-${tab}`}
              onClick={() => setStatusTab(tab)}
              className={`px-3 py-1.5 text-xs font-500 rounded-md transition-all whitespace-nowrap flex items-center gap-1.5 ${
                statusTab === tab ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {TAB_LABELS[tab]}
              {tabCounts[tab] > 0 && (
                <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                  statusTab === tab ? 'bg-accent text-accent-foreground' : 'bg-muted-foreground/20 text-muted-foreground'
                }`}>
                  {tabCounts[tab]}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="relative flex-1">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by count number, site, or operator…"
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all"
          />
        </div>

        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          className="px-3 py-2.5 border border-border rounded-lg text-sm bg-card outline-none focus:ring-2 focus:ring-ring/30 transition-all"
        >
          {SITES.map((s) => <option key={`site-${s}`} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Count list table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-primary text-white text-xs uppercase tracking-wide">
                <th className="px-4 py-3 text-left font-600">Count #</th>
                <th className="px-4 py-3 text-left font-600">Site</th>
                <th className="px-4 py-3 text-left font-600">Count Date</th>
                <th className="px-4 py-3 text-left font-600">Operator</th>
                <th className="px-4 py-3 text-center font-600">Items</th>
                <th className="px-4 py-3 text-center font-600">Variances</th>
                <th className="px-4 py-3 text-left font-600">Status</th>
                <th className="px-4 py-3 text-left font-600">Approved By</th>
                <th className="px-4 py-3 text-center font-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-muted-foreground text-sm">
                    <Icon name="ClipboardDocumentListIcon" size={32} className="mx-auto mb-2 opacity-30" />
                    No stock counts found
                  </td>
                </tr>
              ) : (
                filtered.map((count, idx) => {
                  const netVarianceValue = count.lineItems.reduce((acc, li) => acc + (li.varianceValue ?? 0), 0);
                  return (
                    <tr
                      key={`cnt-row-${count.id}`}
                      className={`border-t border-border hover:bg-muted/40 transition-colors ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'}`}
                    >
                      <td className="px-4 py-3">
                        <span className="font-600 text-secondary text-xs">{count.countNumber}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Icon name="MapPinIcon" size={13} className="text-muted-foreground shrink-0" />
                          <span className="text-xs font-500">{count.site}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{count.countDate}</td>
                      <td className="px-4 py-3 text-xs">{count.createdBy}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="text-xs font-600">{count.totalItems}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {count.variantItems > 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-600 text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                            <Icon name="ExclamationTriangleIcon" size={11} />
                            {count.variantItems}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-600 text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full">
                            <Icon name="CheckIcon" size={11} />
                            None
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={count.status as 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED'} size="sm" />
                        {count.adjustmentPosted && (
                          <span className="ml-1 text-[10px] text-teal-600 font-500">• Adj. Posted</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {count.approvedBy ? (
                          <div>
                            <p className="font-500 text-foreground">{count.approvedBy}</p>
                            <p className="text-[10px]">{count.approvedDate}</p>
                          </div>
                        ) : count.status === 'REJECTED' ? (
                          <span className="text-red-500 text-[10px]">Rejected</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          {count.status === 'PENDING_APPROVAL' && (
                            <button
                              onClick={() => setApprovalCount(count)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-primary text-white rounded-md text-[11px] font-600 hover:bg-primary/90 transition-colors"
                            >
                              <Icon name="CheckCircleIcon" size={13} />
                              Review
                            </button>
                          )}
                          {(count.status === 'APPROVED' || count.status === 'REJECTED') && (
                            <button
                              onClick={() => setReportCount(count)}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-secondary/10 text-secondary border border-secondary/20 rounded-md text-[11px] font-600 hover:bg-secondary/20 transition-colors"
                            >
                              <Icon name="DocumentChartBarIcon" size={13} />
                              Report
                            </button>
                          )}
                          {count.status === 'DRAFT' && (
                            <button
                              onClick={() => toast.info('Continue editing draft count.')}
                              className="flex items-center gap-1 px-2.5 py-1.5 bg-muted text-muted-foreground border border-border rounded-md text-[11px] font-500 hover:bg-muted/80 transition-colors"
                            >
                              <Icon name="PencilSquareIcon" size={13} />
                              Edit
                            </button>
                          )}
                          {netVarianceValue !== 0 && count.status !== 'DRAFT' && (
                            <span className={`text-[10px] font-600 ${netVarianceValue < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                              {netVarianceValue < 0 ? '▼' : '▲'} €{Math.abs(netVarianceValue).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <StockCountEntryModal
        open={entryModalOpen}
        onClose={() => setEntryModalOpen(false)}
        onSubmit={handleNewCount}
      />
      {approvalCount && (
        <StockCountApprovalModal
          open={!!approvalCount}
          count={approvalCount}
          onClose={() => setApprovalCount(null)}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}
      {reportCount && (
        <NonVarianceReportModal
          open={!!reportCount}
          count={reportCount}
          onClose={() => setReportCount(null)}
        />
      )}
    </div>
  );
}
